import { useState, useEffect } from "react";
import { useHttpClient } from "../../../../../shared/hooks/http-hook";
import { useLoading } from "../../../../../shared/context/LoadingContext";
import toast from "react-hot-toast";
import moment from "moment";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Chip,
  Box,
  Card,
  CardContent,
  Grid,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonIcon from "@mui/icons-material/Person";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import MedicationIcon from "@mui/icons-material/Medication";
import PaymentIcon from "@mui/icons-material/Payment";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import * as XLSX from "xlsx";

const RelatorioDiario = ({
  dataFiltro,
  dadosClinica,
  auth,
  acao,
  atualizacoes,
}) => {
  const { startLoading, stopLoading } = useLoading();
  const { sendRequest } = useHttpClient();
  const [dadosRelatorio, setDadosRelatorio] = useState(null);
  const [expandedAccordion, setExpandedAccordion] = useState(false);

  // Buscar dados do relatório quando a ação for 'gerar' ou 'exportar'
  useEffect(() => {
    if (acao && dataFiltro.dataInicial && atualizacoes > 0) {
      buscarDadosRelatorio();
    }
  }, [acao, dataFiltro, atualizacoes]);

  const buscarDadosRelatorio = async () => {
    startLoading();
    try {
      // Para relatório diário, usamos apenas a dataInicial como data específica
      const dataRelatorio = moment(dataFiltro.dataInicial).format("YYYY-MM-DD");

      const formData = new FormData();
      formData.append("dataRelatorio", dataRelatorio);

      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/agenda/gerarRelatorioDiario/${dadosClinica._id}`,
        "POST",
        formData,
        {
          Authorization: "Bearer " + auth.token,
        }
      );

      setDadosRelatorio(responseData);

      if (acao === "exportar") {
        exportarParaExcel(responseData);
      }

      toast.success("Relatório gerado com sucesso!");
    } catch (err) {
      console.error("Erro ao buscar dados do relatório:", err);
      toast.error(
        "Erro ao gerar relatório: " + (err.message || "Erro desconhecido")
      );
    } finally {
      stopLoading();
    }
  };

  const exportarParaExcel = (dados) => {
    try {
      const workbook = XLSX.utils.book_new();

      // Criar dados no formato da imagem - uma linha por atendimento
      const dadosRelatorio = [];

      // Cabeçalho
      dadosRelatorio.push([
        "Nome",
        "Data",
        "Descrição nosológica da causa do atendimento",
        "Tipo Tratamento",
        "Medicamentos prescritos",
        "Quantia Paga",
        "Tipo pagamento",
        "Assinatura OC",
      ]);

      // Processar cada cliente
      dados.clientes.forEach((clienteData) => {
        const cliente = clienteData.cliente;

        // Para cada atendimento do cliente
        clienteData.atendimentos.forEach((atendimento) => {
          // Determinar tipo de tratamento
          let tipoTratamento = "";
          if (clienteData.procedimentosExecutados.length > 0) {
            // Lista de tratamentos executados, separados por vírgula
            const tratamentosExecutados = clienteData.procedimentosExecutados
              .map((proc) => proc.tratamento)
              .filter((t) => t) // Remove valores vazios/null
              .join(", ");

            tipoTratamento = tratamentosExecutados || "Tratamento marcado";
          } else if (atendimento.tratamento === "Avaliação") {
            tipoTratamento = "Avaliação";
          } else if (atendimento.categoria) {
            tipoTratamento = atendimento.categoria;
          } else if (atendimento.tratamento) {
            tipoTratamento = atendimento.tratamento;
          } else {
            tipoTratamento = "Tratamento marcado";
          }

          // Buscar medicamentos prescritos do cliente no dia
          const medicamentos =
            clienteData.medicamentosPrescritos.length > 0
              ? clienteData.medicamentosPrescritos
                  .map((m) => m.medicamento)
                  .join(", ")
              : "**";

          // Buscar pagamentos do cliente no dia
          const pagamentosCliente = clienteData.pagamentosRealizados;
          let quantiaPago = 0;
          let tipoPagamento = "**";

          if (pagamentosCliente.length > 0) {
            // Somar todos os pagamentos do cliente
            quantiaPago = pagamentosCliente.reduce(
              (total, pag) => total + (pag.valor || 0),
              0
            );

            // Determinar tipo de pagamento predominante ou listar múltiplos
            const metodos = [
              ...new Set(
                pagamentosCliente.map((p) => p.metodoPagamento).filter((m) => m)
              ),
            ];
            if (metodos.length === 1) {
              tipoPagamento = metodos[0].toLowerCase();
            } else if (metodos.length > 1) {
              tipoPagamento = metodos.join("+");
            }
          }

          // Adicionar linha ao relatório
          dadosRelatorio.push([
            cliente.nome,
            moment(dados.data).format("DD/M/YYYY"),
            "Tratamento marcado",
            tipoTratamento,
            medicamentos,
            quantiaPago > 0 ? quantiaPago : "**",
            tipoPagamento,
            auth.nome, // Assinatura do usuário logado
          ]);
        });

        // Se cliente não teve atendimentos mas teve outras atividades
        if (
          clienteData.atendimentos.length === 0 &&
          (clienteData.pagamentosRealizados.length > 0 ||
            clienteData.medicamentosPrescritos.length > 0 ||
            clienteData.procedimentosExecutados.length > 0)
        ) {
          let tipoAtividade = "**";
          let quantiaPago = 0;
          let tipoPagamento = "**";

          if (clienteData.pagamentosRealizados.length > 0) {
            tipoAtividade = "Pagamento";
            quantiaPago = clienteData.pagamentosRealizados.reduce(
              (total, pag) => total + (pag.valor || 0),
              0
            );
            const metodos = [
              ...new Set(
                clienteData.pagamentosRealizados
                  .map((p) => p.metodoPagamento)
                  .filter((m) => m)
              ),
            ];
            tipoPagamento =
              metodos.length === 1 ? metodos[0] : metodos.join("+");
          } else if (clienteData.medicamentosPrescritos.length > 0) {
            tipoAtividade = "Prescrição de Medicamentos";
          } else if (clienteData.procedimentosExecutados.length > 0) {
            tipoAtividade = "Procedimentos Executados";
          }

          // Buscar medicamentos prescritos
          const medicamentos =
            clienteData.medicamentosPrescritos.length > 0
              ? clienteData.medicamentosPrescritos
                  .map((m) => m.medicamento)
                  .join(", ")
              : "**";

          dadosRelatorio.push([
            cliente.nome,
            moment(dados.data).format("DD/M/YYYY"),
            tipoAtividade,
            "**",
            medicamentos,
            quantiaPago > 0 ? quantiaPago : "**",
            tipoPagamento,
            auth.nome,
          ]);
        }
      });

      // NOVO: Adicionar resumo financeiro usando dados do backend
      dadosRelatorio.push([]);
      dadosRelatorio.push(["=== RESUMO FINANCEIRO ==="]);

      const resumo = dados.resumoFinanceiro;

      // Mostrar cada método específico
      Object.entries(resumo.metodosPagamento).forEach(([metodo, valor]) => {
        dadosRelatorio.push([
          "",
          "",
          "",
          "",
          "",
          `${metodo}: ${valor.toLocaleString("pt-PT")} CVE`,
          "",
          "",
        ]);
      });

      // Total geral
      dadosRelatorio.push([
        "",
        "",
        "",
        "",
        "",
        `TOTAL GERAL: ${resumo.totalGeral.toLocaleString("pt-PT")} CVE`,
        "",
        "",
      ]);

      // Criar planilha
      const worksheet = XLSX.utils.aoa_to_sheet(dadosRelatorio);

      // Ajustar larguras das colunas
      const columnWidths = [
        { wch: 15 }, // Nome
        { wch: 12 }, // Data
        { wch: 25 }, // Descrição nosológica
        { wch: 20 }, // Tipo Tratamento
        { wch: 20 }, // Medicamentos
        { wch: 15 }, // Quantia Pago
        { wch: 15 }, // Tipo pagamento
        { wch: 15 }, // Assinatura OC
      ];
      worksheet["!cols"] = columnWidths;

      XLSX.utils.book_append_sheet(workbook, worksheet, "Relatório Diário");

      // Salvar arquivo
      const fileName = `Relatorio_Diario_${moment(dados.data).format(
        "DD-MM-YYYY"
      )}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      toast.success("Arquivo Excel exportado com sucesso!");
    } catch (error) {
      console.error("Erro ao exportar para Excel:", error);
      toast.error("Erro ao exportar arquivo Excel");
    }
  };

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  const formatarValor = (valor) => {
    return new Intl.NumberFormat("pt-PT").format(valor || 0) + " CVE";
  };

  const getChipColor = (tipo) => {
    const cores = {
      Agenda: "primary",
      AgendaTratamento: "secondary",
      TratamentoIsolado: "success",
      TransicaoCategoria: "warning",
      TransicaoTratamento: "warning",
    };
    return cores[tipo] || "default";
  };

  const getOrigemColor = (origem) => {
    const cores = {
      agendamento: "primary",
      procedimento: "secondary",
      pagamento: "success",
      prescricao: "info",
      tratamento_isolado: "warning",
    };
    return cores[origem] || "default";
  };

  const getOrigemLabel = (origem) => {
    const labels = {
      agendamento: "Agendado",
      procedimento: "Procedimento",
      pagamento: "Pagamento",
      prescricao: "Prescrição",
      tratamento_isolado: "Urgência",
    };
    return labels[origem] || origem;
  };

  if (!dadosRelatorio) {
    return (
      <Box sx={{ textAlign: "center", mt: 4 }}>
        <Typography variant="h6" color="textSecondary">
          Selecione uma data e clique em "Gerar Relatório" para visualizar os
          dados do dia.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      {/* Header do Relatório */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <AccessTimeIcon sx={{ mr: 1, color: "primary.main" }} />
            <Typography variant="h5" component="h2">
              Relatório Diário -{" "}
              {moment(dadosRelatorio.data).format("DD/MM/YYYY")}
            </Typography>
          </Box>
          <Typography variant="subtitle1" color="textSecondary">
            Clínica: {dadosClinica.nome} | Tipo de Agendamento:{" "}
            {dadosRelatorio.tipoAgendamento}
          </Typography>
        </CardContent>
      </Card>

      {/* Cards de Resumo Geral */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <PersonIcon sx={{ mr: 1, color: "primary.main" }} />
                <Box>
                  <Typography variant="h4">
                    {dadosRelatorio.resumo.totalClientes}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Clientes Atendidos
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <AccessTimeIcon sx={{ mr: 1, color: "secondary.main" }} />
                <Box>
                  <Typography variant="h4">
                    {dadosRelatorio.resumo.totalAtendimentos}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Atendimentos
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <LocalHospitalIcon sx={{ mr: 1, color: "success.main" }} />
                <Box>
                  <Typography variant="h4">
                    {dadosRelatorio.resumo.totalProcedimentos}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Procedimentos
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <MedicationIcon sx={{ mr: 1, color: "warning.main" }} />
                <Box>
                  <Typography variant="h4">
                    {dadosRelatorio.resumo.totalPrescricoes}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Prescrições
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <PaymentIcon sx={{ mr: 1, color: "info.main" }} />
                <Box>
                  <Typography variant="h4">
                    {dadosRelatorio.resumo.totalPagamentos}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Pagamentos
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* NOVO: Resumo Financeiro */}
      {dadosRelatorio.resumoFinanceiro &&
        dadosRelatorio.resumoFinanceiro.totalGeral > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <AccountBalanceWalletIcon
                  sx={{ mr: 1, color: "success.main" }}
                />
                <Typography variant="h6" component="h3">
                  Resumo Financeiro
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Box
                    sx={{
                      textAlign: "center",
                      p: 2,
                      border: "2px solid #4caf50",
                      borderRadius: 1,
                      bgcolor: "#f1f8e9",
                    }}
                  >
                    <Typography
                      variant="h5"
                      color="success.main"
                      fontWeight="bold"
                    >
                      {formatarValor(
                        dadosRelatorio.resumoFinanceiro.totalGeral
                      )}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      fontWeight="bold"
                    >
                      Total Arrecadado
                    </Typography>
                  </Box>
                </Grid>
                {Object.entries(
                  dadosRelatorio.resumoFinanceiro.metodosPagamento
                ).map(([metodo, valor]) => (
                  <Grid item xs={12} sm={6} md={3} key={metodo}>
                    <Box
                      sx={{
                        textAlign: "center",
                        p: 1,
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                      }}
                    >
                      <Typography variant="h6">
                        {formatarValor(valor)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {metodo}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        )}

      {/* Detalhes por Cliente */}
      <Typography variant="h6" sx={{ mb: 2 }}>
        Detalhes por Cliente ({dadosRelatorio.clientes.length})
      </Typography>

      {dadosRelatorio.clientes.length === 0 ? (
        <Card>
          <CardContent>
            <Typography
              variant="body1"
              color="textSecondary"
              textAlign="center"
            >
              Nenhum cliente foi atendido nesta data.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        dadosRelatorio.clientes.map((clienteData, index) => (
          <Accordion
            key={clienteData.cliente._id}
            expanded={expandedAccordion === `cliente-${index}`}
            onChange={handleAccordionChange(`cliente-${index}`)}
            sx={{ mb: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box
                sx={{ display: "flex", alignItems: "center", width: "100%" }}
              >
                <PersonIcon sx={{ mr: 2, color: "primary.main" }} />
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Typography variant="h6">
                      {clienteData.cliente.nome}
                    </Typography>
                    <Chip
                      label={getOrigemLabel(clienteData.origem)}
                      color={getOrigemColor(clienteData.origem)}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    {clienteData.cliente.contacto &&
                      `Tel: ${clienteData.cliente.contacto} | `}
                    Atendimentos: {clienteData.resumoCliente.totalAtendimentos}{" "}
                    | Procedimentos:{" "}
                    {clienteData.resumoCliente.totalProcedimentos} | Total Pago:{" "}
                    {formatarValor(clienteData.resumoCliente.totalPago)}
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                {/* Atendimentos */}
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 1, fontWeight: "bold" }}
                  >
                    📋 Atendimentos ({clienteData.atendimentos.length})
                  </Typography>
                  {clienteData.atendimentos.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Hora</TableCell>
                            <TableCell>Médico</TableCell>
                            <TableCell>Tipo</TableCell>
                            <TableCell>Detalhes</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {clienteData.atendimentos.map((atendimento, idx) => (
                            <TableRow key={idx}>
                              <TableCell>
                                {moment(atendimento.hora).format("HH:mm")}
                              </TableCell>
                              <TableCell>{atendimento.medico?.name}</TableCell>
                              <TableCell>
                                <Chip
                                  label={atendimento.tipo}
                                  color={getChipColor(atendimento.tipo)}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                {atendimento.categoria && (
                                  <div>
                                    <strong>Categoria:</strong>{" "}
                                    {atendimento.categoria}
                                  </div>
                                )}
                                {atendimento.tratamento && (
                                  <div>
                                    <strong>Tratamento:</strong>{" "}
                                    {atendimento.tratamento}
                                  </div>
                                )}
                                {atendimento.evolucao && (
                                  <div>
                                    <strong>Evolução:</strong>{" "}
                                    {atendimento.evolucao}
                                  </div>
                                )}
                                {atendimento.observacoes && (
                                  <div>
                                    <strong>Obs:</strong>{" "}
                                    {atendimento.observacoes}
                                  </div>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Nenhum atendimento registrado.
                    </Typography>
                  )}
                </Grid>

                {/* Procedimentos */}
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 1, fontWeight: "bold" }}
                  >
                    🦷 Procedimentos (
                    {clienteData.procedimentosExecutados.length})
                  </Typography>
                  {clienteData.procedimentosExecutados.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Tratamento</TableCell>
                            <TableCell>Dente/Faces</TableCell>
                            <TableCell>Médico</TableCell>
                            <TableCell>Origem</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {clienteData.procedimentosExecutados.map(
                            (proc, idx) => (
                              <TableRow key={idx}>
                                <TableCell>{proc.tratamento}</TableCell>
                                <TableCell>
                                  {proc.dente && `Dente: ${proc.dente}`}
                                  {proc.faces &&
                                    proc.faces.length > 0 &&
                                    ` | Faces: ${proc.faces.join(", ")}`}
                                </TableCell>
                                <TableCell>{proc.medico}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={proc.origem}
                                    variant="outlined"
                                    size="small"
                                  />
                                </TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Nenhum procedimento executado.
                    </Typography>
                  )}
                </Grid>

                {/* Medicamentos */}
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 1, fontWeight: "bold" }}
                  >
                    💊 Medicamentos ({clienteData.medicamentosPrescritos.length}
                    )
                  </Typography>
                  {clienteData.medicamentosPrescritos.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Medicamento</TableCell>
                            <TableCell>Posologia</TableCell>
                            <TableCell>Duração</TableCell>
                            <TableCell>Médico</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {clienteData.medicamentosPrescritos.map(
                            (med, idx) => (
                              <TableRow key={idx}>
                                <TableCell>{med.medicamento}</TableCell>
                                <TableCell>{med.posologia}</TableCell>
                                <TableCell>{med.duracao}</TableCell>
                                <TableCell>{med.medico}</TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Nenhum medicamento prescrito.
                    </Typography>
                  )}
                </Grid>

                {/* Pagamentos */}
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 1, fontWeight: "bold" }}
                  >
                    💰 Pagamentos ({clienteData.pagamentosRealizados.length})
                  </Typography>
                  {clienteData.pagamentosRealizados.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Valor</TableCell>
                            <TableCell>Método</TableCell>
                            <TableCell>Tipo</TableCell>
                            <TableCell>Descrição</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {clienteData.pagamentosRealizados.map((pag, idx) => (
                            <TableRow key={idx}>
                              <TableCell>{formatarValor(pag.valor)}</TableCell>
                              <TableCell>{pag.metodoPagamento}</TableCell>
                              <TableCell>
                                <Chip
                                  label={pag.tipo}
                                  variant="outlined"
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                {pag.descricao}
                                {pag.nrParcela && ` (${pag.nrParcela})`}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Nenhum pagamento realizado.
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </Box>
  );
};

export default RelatorioDiario;
